import { useStacklineTheme } from '@stackline/ui';
import React, { useRef } from 'react';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { calculatePercentChange, getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector/types';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import ReactDOMServer from 'react-dom/server';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import _capitalize from 'lodash/capitalize';
import { isTopicOrTopicGroupType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import saveAs from 'file-saver';
import Papa from 'papaparse';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';
import { ChartLegendMetadata } from 'src/utils/chartContext/types';

export interface TrafficBarChartsProps {
  organicTraffic: {
    current: number;
    comparison: number;
  };
  paidTraffic: {
    current: number;
    comparison: number;
  };
  otherTraffic: {
    current: number;
    comparison: number;
  };
  /** Total traffic for current period (same source as spline legend for alignment) */
  totalTraffic: number;
  /** Total traffic for comparison period (same source as spline legend for alignment) */
  totalTrafficComparison: number;
  loading: boolean;
  externalContainerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Shows bar charts for total traffic page
 */
export const TrafficBarCharts = ({
  organicTraffic,
  paidTraffic,
  otherTraffic,
  totalTraffic,
  totalTrafficComparison,
  loading,
  externalContainerRef
}: TrafficBarChartsProps) => {
  const formatMetric = useMetricFormatter();
  const theme = useStacklineTheme();
  const primaryDisplayName = useAppSelector((state) => state.mainTimePeriod.displayName);
  const comparisonDisplayName = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const chartRef = useRef(null);

  // Use external ref for export functionality if provided, otherwise use internal ref
  const exportRef = externalContainerRef || chartRef;

  const chartValues = [...Object.values(organicTraffic), ...Object.values(paidTraffic), ...Object.values(otherTraffic)];
  const entityType = useMainEntityType();
  const isTopicOrTopicGroup = isTopicOrTopicGroupType(entityType);
  const entityName = useAppSelector((state) => state.entityService.mainEntity.name);

  const chartOptionsOverride = (defaultOptions: Highcharts.Options): Highcharts.Options => ({
    ...defaultOptions,
    chart: {
      ...defaultOptions.chart,
      height: 250,
      marginBottom: 0
    },
    yAxis: {
      ...defaultOptions.yAxis,
      max: Math.max(...chartValues) * 1.1
    },
    // @ts-ignore
    xAxis: defaultOptions?.xAxis?.map((xAxis) => ({
      ...xAxis,
      max: isTopicOrTopicGroup ? 2 : 3,
      labels: {
        rotation: 0,
        align: 'center',
        useHTML: true,
        formatter() {
          return ReactDOMServer.renderToStaticMarkup(
            <div
              style={{
                fontSize: '12px',
                color: theme.colors.primary,
                fontFamily: 'Roboto',
                marginTop: '12px'
              }}
            >
              {this.value.name}
            </div>
          );
        }
      }
    })),
    series: [
      {
        ...defaultOptions.series[0],
        // @ts-ignore
        data: defaultOptions.series[0]?.data?.map?.(([label, value], index) => ({
          name: label,
          y: value,
          color: [theme.colors.primaryLight, theme.colors.success, theme.colors.accentTeal][index]
        }))
      }
    ],
    plotOptions: {
      ...defaultOptions.plotOptions,
      column: {
        pointPadding: 0.2,
        groupPadding: 0.3
      }
    }
  });

  const categories = [
    {
      id: 'Organic',
      name: 'Organic'
    },
    {
      id: 'Paid Search',
      name: 'Paid Search'
    },
    ...(!isTopicOrTopicGroup ? [{ id: 'Other', name: 'Other' }] : [])
  ];

  const noData =
    !organicTraffic.current &&
    !paidTraffic.current &&
    !otherTraffic.current &&
    !organicTraffic.comparison &&
    !paidTraffic.comparison &&
    !otherTraffic.comparison;

  if (noData && !loading) {
    return (
      <BeaconChartWithLegend
        title=""
        showTitleSummary={false}
        primaryLegendProps={{
          displayName: primaryDisplayName
        }}
        comparisonLegendProps={{
          displayName: comparisonDisplayName
        }}
      >
        <NoDataPlaceHolder
          title="Data currently unavailable"
          description="There is currently no data matching your selected criteria. Please try another query to view insights."
          flexProps={{ margin: '40px auto', width: '550px' }}
        />
      </BeaconChartWithLegend>
    );
  }

  return (
    <div
      ref={chartRef}
      style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative'
      }}
    >
      {[
        {
          displayName: comparisonDisplayName,
          traffic: totalTrafficComparison,
          organicTrafficValue: organicTraffic.comparison,
          paidTrafficValue: paidTraffic.comparison,
          otherTrafficValue: otherTraffic.comparison
        },
        {
          displayName: primaryDisplayName,
          traffic: totalTraffic,
          trafficPercentChange: calculatePercentChange(totalTraffic, totalTrafficComparison),
          organicTrafficValue: organicTraffic.current,
          paidTrafficValue: paidTraffic.current,
          otherTrafficValue: otherTraffic.current
        }
      ].map(
        (
          { displayName, traffic, trafficPercentChange, organicTrafficValue, paidTrafficValue, otherTrafficValue },
          index
        ) => {
          // Chart context IDs for Advisor integration
          // index 0 = comparison period, index 1 = current period
          const chartContextId = index === 0 ? CHART_NAMES.TRAFFIC_BAR_COMPARISON : CHART_NAMES.TRAFFIC_BAR_CURRENT;

          const formattedTraffic = formatMetric(traffic, METRICTYPE.VOLUME, { showFullValue: false, decimalPlaces: 2 });
          const formattedComparisonTraffic = formatMetric(totalTrafficComparison, METRICTYPE.VOLUME, {
            showFullValue: false,
            decimalPlaces: 2
          });
          const barLegendMetadata: ChartLegendMetadata = {
            mainTimePeriodValue: String(formattedTraffic),
            comparisonTimePeriodValue: index > 0 ? String(formattedComparisonTraffic) : '',
            rawPercentChange: index > 0 ? Number(trafficPercentChange) || 0 : 0,
            formattedPercentChange:
              index > 0
                ? String(formatMetric(Math.abs(trafficPercentChange ?? 0), METRICTYPE.PERCENT, { decimalPlaces: 2 }))
                : '',
            direction: (trafficPercentChange ?? 0) >= 0 ? 'up' : 'down',
            valueType: 'total'
          };

          return (
            <div key={displayName} style={{ width: '300px', ...(externalContainerRef ? { height: '380px' } : {}) }}>
              <BeaconChartWithLegend
                convertSeriesToDelimitedData={() => {
                  const series = [
                    {
                      'Traffic Type': 'Organic Traffic',
                      [comparisonDisplayName]: organicTraffic.comparison,
                      [primaryDisplayName]: organicTraffic.current
                    },
                    {
                      'Traffic Type': 'Paid Traffic',
                      [comparisonDisplayName]: paidTraffic.comparison,
                      [primaryDisplayName]: paidTraffic.current
                    },
                    ...(isTopicOrTopicGroup
                      ? []
                      : [
                          {
                            'Traffic Type': 'Other Traffic',
                            [comparisonDisplayName]: otherTraffic.comparison,
                            [primaryDisplayName]: otherTraffic.current
                          }
                        ]),
                    {
                      'Traffic Type': 'Total Traffic',
                      [comparisonDisplayName]: totalTrafficComparison,
                      [primaryDisplayName]: totalTraffic
                    }
                  ];

                  try {
                    const csv = Papa.unparse(series);
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                    saveAs(blob, 'chart_data.csv');
                  } catch (e) {
                    console.error('error: ', e);
                  }
                  return Promise.resolve();
                }}
                title=""
                showTitleSummary={false}
                primaryLegendProps={{
                  displayName,
                  legendIndicatorProps: {
                    showDot: false
                  },
                  metric: `${formatMetric(traffic, METRICTYPE.VOLUME, {
                    showFullValue: false,
                    decimalPlaces: 2
                  })}`,
                  direction: index > 0 ? getDirection(trafficPercentChange) : undefined,
                  change:
                    index > 0
                      ? `${formatMetric(Math.abs(trafficPercentChange), METRICTYPE.PERCENT, {
                          decimalPlaces: 2
                        })}`
                      : undefined
                }}
                loading={loading}
                loadingComponent={<SlSkeleton variant="rounded" height={330} width={280} />}
                showExportMenu={index > 0 && getAppName() !== AppName.Atlas}
                saveImageRef={exportRef}
                selectedMainMetric={
                  {
                    displayName: `${_capitalize(entityType)} - ${entityName} - Traffic` // Sets the export file name
                  } as any
                }
              >
                <TopEntityChartV2
                  chartSeries={
                    [
                      {
                        type: 'column',
                        color: theme.colors.primaryLight,
                        data: [
                          ['Organic', organicTrafficValue],
                          ['Paid Search', paidTrafficValue],
                          ...(!isTopicOrTopicGroup ? [['Other', otherTrafficValue]] : [])
                        ],
                        categories,
                        metricType: METRICTYPE.VOLUME
                      }
                    ] as any[]
                  }
                  chartOptionsOverride={chartOptionsOverride}
                  containerSx={{
                    left: isTopicOrTopicGroup ? '-32px' : '-24px',
                    position: 'relative'
                  }}
                  chartContextId={chartContextId}
                  legendMetadata={barLegendMetadata}
                />
              </BeaconChartWithLegend>
            </div>
          );
        }
      )}
    </div>
  );
};
